import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { TOPICS, STARTER_TOPICS, answer, answerTopic, topicGroups } from '../utils/helpMatcher';

const OPENING_TEXT = {
  financial: 'Ask how a number is worked out, what a term means, or where to change something. Or browse Topics below.',
  realestate: 'Ask how a real estate number is worked out, what a term means, or where to change something. Or browse Topics below.',
  general: 'Hi. I can explain how the planning and real estate tools work. Ask a question, or browse Topics below.',
};
const CONTEXT_LABEL = { financial: 'Financial Planning', realestate: 'Real Estate Analysis', general: 'Ultronic Terminal' };

const opening = (context) => ({
  type: 'bot',
  text: OPENING_TEXT[context],
  chips: STARTER_TOPICS[context],
  chipsLabel: 'Common questions',
});

const CHIP = 'min-h-[44px] text-left text-xs font-mono bg-surface-elevated border border-surface-border hover:border-terminal-dark-green focus-visible:border-terminal-green text-txt-secondary hover:text-terminal-cyan px-3 py-2 rounded transition-colors';
const BAR_BUTTON = 'min-h-[44px] px-3 text-xs font-mono uppercase tracking-wider transition-colors';

const ChatbotWidget = () => {
  const location = useLocation();
  const context = location.pathname.startsWith('/financial-planning')
    ? 'financial'
    : location.pathname.startsWith('/real-estate') || location.pathname.startsWith('/results')
    ? 'realestate'
    : 'general';

  const [isOpen, setIsOpen] = useState(false);
  const [showTopics, setShowTopics] = useState(false);
  const [messages, setMessages] = useState(() => [opening(context)]);
  const [inputValue, setInputValue] = useState('');
  // The topic under discussion, so "why?" and "more" stay on it.
  const thread = useRef({ topicId: null, moreShown: false });
  // Bumped on Start over, so a reply already on its way is dropped.
  const generation = useRef(0);
  const scrollRef = useRef(null);
  const lastMessageRef = useRef(null);
  const inputRef = useRef(null);

  // Moving to another module refreshes the opening suggestions, but never
  // wipes a conversation that is under way.
  useEffect(() => {
    setMessages((current) => (current.length <= 1 ? [opening(context)] : current));
  }, [context]);

  // A new page closes the panel, so it never sits over what the person went there to see.
  useEffect(() => {
    setIsOpen(false);
    setShowTopics(false);
  }, [location.pathname]);

  // Bring the start of the newest message into view, so a long answer is
  // read from its first line.
  useEffect(() => {
    const box = scrollRef.current;
    const last = lastMessageRef.current;
    if (box && last) box.scrollTop = Math.max(0, last.offsetTop - 8);
  }, [messages, showTopics, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (showTopics) setShowTopics(false); else setIsOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, showTopics]);

  const deliver = useCallback((question, result) => {
    if (result.kind === 'topic') thread.current = { topicId: result.topicId, moreShown: false };
    else if (result.kind === 'more') thread.current = { ...thread.current, moreShown: true };

    const sentIn = generation.current;
    setShowTopics(false);
    setMessages((current) => [...current, { type: 'user', text: question }]);
    setTimeout(() => {
      if (generation.current !== sentIn) return;
      setMessages((current) => [...current, { type: 'bot', text: result.text, chips: result.chips, chipsLabel: result.chipsLabel }]);
    }, 250);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const question = inputValue.trim();
    if (!question) return;
    deliver(question, answer(question, { lastTopicId: thread.current.topicId, moreShown: thread.current.moreShown, context }));
    setInputValue('');
  };

  const askTopic = (topicId) => deliver(TOPICS[topicId].q, answerTopic(topicId));

  const startOver = () => {
    generation.current += 1;
    thread.current = { topicId: null, moreShown: false };
    setShowTopics(false);
    setInputValue('');
    setMessages([opening(context)]);
    inputRef.current?.focus();
  };

  return (
    <>
      <div className="fixed bottom-6 right-6 z-50" style={{ marginBottom: 'env(safe-area-inset-bottom)' }}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="bg-surface-elevated border border-terminal-dark-green hover:border-terminal-green text-terminal-green rounded-full p-3 transition-all duration-200 hover:shadow-glow-green-sm group"
          aria-label={isOpen ? 'Close help assistant' : 'Open help assistant'}
          aria-expanded={isOpen}
        >
          {isOpen ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-5 h-5 group-hover:drop-shadow-[0_0_6px_var(--terminal-green)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          )}
        </button>
      </div>

      {isOpen && (
        <div
          className="fixed bottom-24 right-4 left-4 sm:left-auto sm:right-6 sm:w-[420px] h-[560px] max-h-[calc(100dvh-8rem)] terminal-card p-0 z-50 flex flex-col overflow-hidden"
          role="dialog"
          aria-label="Help assistant"
        >
          <div className="bg-surface-elevated pl-4 pr-1 py-1 border-b border-surface-border flex items-center justify-between flex-shrink-0">
            <div>
              <h3 className="font-display font-bold text-terminal-green uppercase tracking-wider text-sm crt-glow">
                Help Assistant
              </h3>
              <p className="text-txt-muted font-mono text-xs mt-0.5">{CONTEXT_LABEL[context]}</p>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              aria-label="Close help assistant"
              className="min-w-[44px] min-h-[44px] flex items-center justify-center text-txt-muted hover:text-terminal-green transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {showTopics ? (
            <div id="help-topics" className="flex-1 min-h-[72px] overflow-y-auto p-3 terminal-scrollbar">
              <p className="text-txt-secondary font-mono text-xs mb-3">Pick a question to see the answer.</p>
              {topicGroups(context).map((group) => (
                <section key={group.id} className="mb-4" aria-labelledby={`help-group-${group.id}`}>
                  <h4 id={`help-group-${group.id}`} className="font-display font-bold text-terminal-amber uppercase tracking-wider text-xs mb-1.5">
                    {group.title}
                  </h4>
                  <div className="flex flex-col gap-1.5">
                    {group.topics.map((topic) => (
                      <button key={topic.id} type="button" onClick={() => askTopic(topic.id)} className={`${CHIP} w-full`}>
                        {topic.label}
                      </button>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          ) : (
            <div ref={scrollRef} className="relative flex-1 min-h-[72px] overflow-y-auto p-3 space-y-2.5 terminal-scrollbar" role="log" aria-live="polite">
              {messages.map((msg, index) => {
                const isLast = index === messages.length - 1;
                return (
                  <div key={index} ref={isLast ? lastMessageRef : null}>
                    <div className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[88%] px-3 py-2 rounded-lg font-mono text-xs leading-relaxed whitespace-pre-line ${
                        msg.type === 'user'
                          ? 'bg-terminal-dark-green/30 border border-terminal-dark-green text-terminal-green rounded-br-none'
                          : 'bg-surface-elevated border border-surface-border text-txt-primary rounded-bl-none'
                      }`}>
                        {msg.text}
                      </div>
                    </div>
                    {msg.chips?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-mono uppercase tracking-wider text-txt-muted mb-1.5">{msg.chipsLabel}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {msg.chips.map((topicId) => (
                            <button key={topicId} type="button" onClick={() => askTopic(topicId)} className={CHIP}>
                              {TOPICS[topicId].q}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex items-center justify-between px-1 border-t border-surface-border flex-shrink-0">
            <button
              type="button"
              onClick={() => setShowTopics((value) => !value)}
              aria-expanded={showTopics}
              aria-controls="help-topics"
              className={`${BAR_BUTTON} text-terminal-cyan hover:text-terminal-green`}
            >
              {showTopics ? 'Back to chat' : 'Topics'}
            </button>
            <button
              type="button"
              onClick={startOver}
              disabled={messages.length <= 1 && !showTopics}
              className={`${BAR_BUTTON} text-txt-secondary hover:text-terminal-green disabled:opacity-40 disabled:hover:text-txt-secondary`}
            >
              Start over
            </button>
          </div>

          <form onSubmit={handleSubmit} className="px-3 pb-3 flex-shrink-0">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Ask a question..."
                aria-label="Ask a question"
                className="terminal-input flex-1 min-w-0 rounded-lg px-3 py-2 text-base sm:text-xs font-mono"
              />
              <button
                type="submit"
                className="glow-btn glow-btn-green px-3 py-2 rounded-lg text-xs font-mono uppercase tracking-wider"
              >
                Send
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatbotWidget;
